import { Service } from 'constants/usercentrics';
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { Direction } from 'types/Utilities';
import { isServiceAllowed } from 'components/Common/CookieNotification';
import { EmbedPlaceholder } from 'components/Shared/EmbedPlaceholder';
import RichText from 'components/ui/ContentBlocks/RichText';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import { CookieContext } from 'context/cookie';
import { useTranslation } from 'hooks/useTranslation';
import type { ClinicVideo } from 'types/Clinic';

type Props = {
  videos?: ClinicVideo[];
};

const Testimonials = ({ videos: _videos }: Props): JSX.Element | null => {
  const { locale, t } = useTranslation();
  const { cookies } = useContext(CookieContext);

  const [videoIdx, setVideoIdx] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);

  const showVideoSubtitles = locale !== 'en' ? 1 : 0;
  const filteredVideos = _videos?.filter((item) => !item?.disableLocale);

  useEffect(() => {
    const serviceAllowed = isServiceAllowed(cookies, Service.YOUTUBE);

    setIsAllowed(serviceAllowed.serviceStatus);
  }, [cookies]);

  if (!filteredVideos?.length) {
    return null;
  }

  return (
    <div id="videos" className="border-t border-silver-light lg:pb-16">
      <div className="max-w-screen-xl mx-auto px-5 md:px-4 lg:mt-18">
        <div className="lg:grid grid-cols-3">
          <div className="flex justify-between items-center lg:items-start">
            <h2 className="adelle-bold-section-headline">
              {t('clinicPage.videos')}
            </h2>
            <Icon
              type="Arrow"
              direction={isCollapsed ? Direction.BOTTOM : Direction.TOP}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
              className="lg:hidden h-4 w-4 mb-6 text-navy stroke-current mt-12"
              color="#155263"
            />
          </div>
          {!isAllowed ? (
            <EmbedPlaceholder service={Service.YOUTUBE} spacing="mb-6" />
          ) : (
            <>
              {_videos && filteredVideos ? (
                <div
                  className={clsx(
                    isCollapsed && 'hidden',
                    'lg:block col-span-2 text-charcoal',
                  )}
                >
                  <div className="-mx-3 md:mx-0">
                    <LazyLoad classNamePrefix="video" offset={200} once>
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${filteredVideos[videoIdx]?.youtubeVideoId}?rel=0&cc_load_policy=${showVideoSubtitles}`}
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full sm:mb-6 aspect-video"
                      />
                    </LazyLoad>
                  </div>
                  <p
                    className="font-bold text-lg md:text-2xl mb-3 lg:mb-2 mt-6 md:mt-0"
                    dangerouslySetInnerHTML={{
                      __html: filteredVideos[videoIdx]?.title || '',
                    }}
                  />
                  {filteredVideos[videoIdx]?.descriptionContent && (
                    <div className="mb-10 lg:mb-8 leading-8 md:leading-6">
                      <RichText
                        fields={filteredVideos[videoIdx]?.descriptionContent}
                      />
                    </div>
                  )}
                  {filteredVideos?.length >= 2 && (
                    <>
                      <p className="font-bold mb-4 md:mb-2">
                        {t('clinicPage.moreVideos')}
                      </p>
                      <div className="flex flex-wrap">
                        {filteredVideos.map((video, i) => (
                          <div
                            key={i}
                            onClick={() => setVideoIdx(i)}
                            className="cursor-pointer rounded-sm overflow-hidden mr-4 mb-4 w-28 md:w-40"
                          >
                            <Image
                              src={`https://img.youtube.com/vi/${video?.youtubeVideoId}/mqdefault.jpg`}
                              className="opacity-50 hover:opacity-100 transition duration-300 ease-in-out"
                              alt={video?.title || ''}
                              height={200}
                              width={400}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
